import { useEffect, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Lock, Download, Mail, ArrowLeft, CheckCircle, FileSpreadsheet } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { usePeriodStore } from '@/stores/periodStore'
import { useExportController } from '@/hooks/useExportController'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { SpinnerIcon } from '@/components/ui/SpinnerIcon'
import { ExportSummaryHeader } from '@/components/export/ExportSummaryHeader'
import { ExportDriverTable } from '@/components/export/ExportDriverTable'
import { ExportDialog } from '@/components/common/dialogs/ExportDialog'
import { DataTableColumn } from '@/components/common/DataTable'
import { LockedSalaryData } from '@/types/lockedSalaryData'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'

export function PeriodExportPage() {
  const { periodId } = useParams<{ periodId: string }>()
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const { periods, loading, error, loadPeriods, getPeriod, setCurrentPeriod } = usePeriodStore()

  // Export controller
  const controller = useExportController(currentUser, periodId)

  // Export dialog state
  const [showExportDialog, setShowExportDialog] = useState(false)
  const [exportColumns, setExportColumns] = useState<DataTableColumn<LockedSalaryData>[]>([])

  // Handle columns ready from table
  const handleColumnsReady = useCallback((columns: DataTableColumn<LockedSalaryData>[]) => {
    setExportColumns(columns)
  }, [])

  // Load periods if not loaded
  useEffect(() => {
    if (currentUser && periods.length === 0 && !loading) {
      loadPeriods(currentUser)
    }
  }, [currentUser, periods.length, loading, loadPeriods])

  // Set current period in store
  useEffect(() => {
    if (periodId) {
      setCurrentPeriod(periodId)
    }
  }, [periodId, setCurrentPeriod])

  const periodData = periodId ? getPeriod(periodId) : undefined

  // Access control - only locked periods can access export
  useEffect(() => {
    if (!periodData || !periodId) return

    if (periodData.status !== 'locked') {
      // Draft periods cannot access export - redirect to review
      navigate(`/periods/${periodId}/review`, { replace: true })
    }
  }, [periodData, periodId, navigate])

  const handleBackToPeriods = () => {
    navigate('/periods')
  }

  // Loading state
  if (loading || controller.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center py-12">
        <SpinnerIcon size="lg" className="text-blue-600" />
      </div>
    )
  }

  // Error state
  if (error || controller.error) {
    return (
      <div className="flex flex-1 items-center justify-center py-12">
        <p className="text-red-600">{error || controller.error}</p>
      </div>
    )
  }

  // Period not found
  if (!periodData && periods.length > 0) {
    return (
      <div className="flex flex-1 items-center justify-center py-12">
        <p className="text-red-600">Perioden hittades inte</p>
      </div>
    )
  }

  // Still loading periods
  if (!periodData) {
    return (
      <div className="flex flex-1 items-center justify-center py-12">
        <SpinnerIcon size="lg" className="text-blue-600" />
      </div>
    )
  }

  const periodName = periodData.name || periodId || 'Period'
  const progressPercent =
    controller.bulkEmailProgress.total > 0
      ? (controller.bulkEmailProgress.current / controller.bulkEmailProgress.total) * 100
      : 0
  const pdfProgressPercent =
    controller.bulkPdfProgress.total > 0
      ? (controller.bulkPdfProgress.current / controller.bulkPdfProgress.total) * 100
      : 0

  return (
    <div className="space-y-6">
      {/* Header with back button */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={handleBackToPeriods}
          className="gap-2 -ml-2 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
          Alla perioder
        </Button>
      </div>

      {/* Success state card */}
      <Card className="border-green-200 bg-green-50/50">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-4">
            <div className="rounded-full bg-green-100 p-4">
              <CheckCircle className="h-12 w-12 text-green-600" />
            </div>
          </div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <CardTitle className="text-2xl">{periodName}</CardTitle>
            <Badge variant="default" className="bg-green-600">
              <Lock className="h-3 w-3 mr-1" />
              Låst
            </Badge>
          </div>
          <CardDescription className="text-base">
            Perioden är färdigbehandlad och låst. Lönedata är redo att exporteras.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Summary statistics */}
      <ExportSummaryHeader totals={controller.totals} emailStats={controller.emailStats} />

      {/* Bulk actions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Exportera lönespecifikationer</CardTitle>
          <CardDescription>
            Skicka email, ladda ner PDF:er eller exportera lönedata för alla förare i perioden
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              variant="outline"
              className="flex-1"
              onClick={controller.openBulkEmailConfirm}
              disabled={controller.emailStats.notYetSent === 0}
            >
              <Mail className="h-4 w-4 mr-2" />
              {controller.emailStats.notYetSent > 0
                ? `Skicka email (${controller.emailStats.notYetSent} kvar)`
                : 'Alla email skickade'}
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={controller.downloadAllPdfs}
              disabled={controller.lockedData.length === 0 || controller.bulkPdfProgress.isRunning}
            >
              <Download className="h-4 w-4 mr-2" />
              Ladda ner alla PDF:er ({controller.lockedData.length})
            </Button>
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setShowExportDialog(true)}
              disabled={controller.lockedData.length === 0}
            >
              <FileSpreadsheet className="h-4 w-4 mr-2" />
              Exportera data
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Driver table */}
      <ExportDriverTable
        data={controller.lockedData}
        sendingEmailTo={controller.sendingEmailTo}
        downloadingPdfFor={controller.downloadingPdfFor}
        onSendEmail={controller.sendEmailToDriver}
        onDownloadPdf={controller.downloadPdfForDriver}
        onColumnsReady={handleColumnsReady}
      />

      {/* Export dialog for CSV/Excel/PDF */}
      <ExportDialog
        open={showExportDialog}
        onOpenChange={setShowExportDialog}
        data={controller.lockedData}
        columns={exportColumns}
        baseFilename={`lonedata_${periodId}`}
        title="Exportera lönedata"
        onExportPdf={controller.downloadAllPdfs}
      />

      {/* Bulk email confirmation dialog */}
      <AlertDialog
        open={controller.showBulkEmailConfirm}
        onOpenChange={(open) => {
          if (!open) controller.closeBulkEmailConfirm()
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Skicka lönespecifikationer via email</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>
                Du kommer att skicka lönespecifikationer till{' '}
                <strong>{controller.emailStats.notYetSent}</strong> förare som inte redan fått sina
                specifikationer.
              </p>
              {controller.emailStats.alreadySent > 0 && (
                <p className="text-muted-foreground">
                  {controller.emailStats.alreadySent} förare har redan fått email och kommer inte att få
                  något nytt.
                </p>
              )}
              {controller.emailStats.withoutEmail > 0 && (
                <p className="text-muted-foreground">
                  {controller.emailStats.withoutEmail} förare saknar e-postadress.
                </p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={controller.sendBulkEmails}>Skicka email</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk email progress dialog */}
      <AlertDialog
        open={controller.bulkEmailProgress.isOpen}
        onOpenChange={(open) => {
          if (!open && !controller.bulkEmailProgress.isRunning) {
            controller.closeBulkEmailProgress()
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {controller.bulkEmailProgress.isRunning ? 'Skickar email...' : 'Email skickade'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <Progress value={progressPercent} />
                <p>
                  {controller.bulkEmailProgress.current} av {controller.bulkEmailProgress.total} email
                  skickade
                </p>
                {controller.bulkEmailProgress.errors.length > 0 && (
                  <div className="text-red-600 text-sm">
                    <p className="font-medium">Misslyckade:</p>
                    <ul className="list-disc list-inside">
                      {controller.bulkEmailProgress.errors.slice(0, 5).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                      {controller.bulkEmailProgress.errors.length > 5 && (
                        <li>...och {controller.bulkEmailProgress.errors.length - 5} till</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          {!controller.bulkEmailProgress.isRunning && (
            <AlertDialogFooter>
              <AlertDialogAction onClick={controller.closeBulkEmailProgress}>Stäng</AlertDialogAction>
            </AlertDialogFooter>
          )}
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk PDF progress dialog */}
      <AlertDialog
        open={controller.bulkPdfProgress.isOpen}
        onOpenChange={(open) => {
          if (!open && !controller.bulkPdfProgress.isRunning) {
            controller.closeBulkPdfProgress()
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {controller.bulkPdfProgress.isRunning ? 'Genererar PDF:er...' : 'PDF:er klara'}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <Progress value={pdfProgressPercent} />
                <p>
                  {controller.bulkPdfProgress.current} av {controller.bulkPdfProgress.total} PDF:er
                  genererade
                </p>
                {controller.bulkPdfProgress.errors.length > 0 && (
                  <div className="text-red-600 text-sm">
                    <p className="font-medium">Misslyckade:</p>
                    <ul className="list-disc list-inside">
                      {controller.bulkPdfProgress.errors.slice(0, 5).map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                      {controller.bulkPdfProgress.errors.length > 5 && (
                        <li>...och {controller.bulkPdfProgress.errors.length - 5} till</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          {!controller.bulkPdfProgress.isRunning && (
            <AlertDialogFooter>
              <AlertDialogAction onClick={controller.closeBulkPdfProgress}>Stäng</AlertDialogAction>
            </AlertDialogFooter>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
